import React, { useState, useEffect } from 'react';
import { Shield, Save, Check, Search, ChevronRight } from 'lucide-react';
import { useRoleList, useRole } from './shared/services/useRoleService';
import { useRoleForm, PERMISSION_STRUCTURE } from './shared/hooks/useRoleForm';
import { useNotification } from '../../contexts/NotificationContext';
import RoleForm from './shared/sections/RoleForm';

const PermissionManagement = () => {
    const { showNotification } = useNotification();
    const { roles, loading: rolesLoading } = useRoleList();
    const [selectedRoleId, setSelectedRoleId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // When a role is selected, we use the useRole hook to fetch its details
    const { existingData, loading: roleLoading, submit } = useRole(selectedRoleId);
    const { formData, setInitialData, handleNameChange, handleDescriptionChange, togglePermission, handleQuickAction, buildPayload } = useRoleForm();

    useEffect(() => {
        if (existingData) {
            setInitialData(existingData);
        }
    }, [existingData, setInitialData]);

    const handleSave = async () => {
        if (!selectedRoleId) return;
        setIsSaving(true);
        try {
            const res = await submit(buildPayload());
            if (res.data.status === 'success' || res.status === 200) {
                showNotification('success', `Permissions for "${formData.name}" updated successfully!`);
            } else {
                showNotification('error', res.data.message || 'An error occurred.');
            }
        } catch (err) {
            showNotification('error', err.response?.data?.message || 'Server error.');
        } finally {
            setIsSaving(false);
        }
    };

    if (rolesLoading) {
        return (
            <div className="p-8 text-center text-slate-400">
                <div className="animate-spin h-8 w-8 border-4 border-teal-500 border-t-transparent rounded-full mx-auto mb-4" />
                <p className="font-bold uppercase tracking-widest text-xs">Loading Roles...</p>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 space-y-8 bg-slate-50/50 min-h-screen">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div className="flex items-center gap-4 w-full md:w-auto">
                    {selectedRoleId && (
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm shadow-lg transition-all ${
                                isSaving ? 'bg-teal-300' : 'bg-teal-500 hover:bg-teal-600'
                            } text-white`}
                        >
                            <Save className="w-4 h-4" />
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                    )}

                    <div className="flex items-center gap-3 bg-white p-2 rounded-2xl shadow-sm border border-slate-100 flex-1 md:flex-none">
                        <Shield className="w-5 h-5 text-teal-600 ml-3" />
                        <select
                            value={selectedRoleId || ''}
                            onChange={(e) => setSelectedRoleId(e.target.value)}
                            className="bg-transparent border-none focus:ring-0 text-sm font-bold text-slate-700 pr-10 py-2 outline-none cursor-pointer min-w-[200px]"
                        >
                            <option value="" disabled>Select a Role to Manage</option>
                            {roles.map(role => (
                                <option key={role.role_id} value={role.role_id}>{role.name}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {!selectedRoleId ? (
                <div className="flex flex-col items-center justify-center space-y-6 py-20 bg-white rounded-[3rem] border border-slate-100 shadow-sm">
                    <div className="p-8 bg-slate-50 rounded-full">
                        <Shield className="w-16 h-16 text-slate-200" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-xl font-bold text-slate-800 mb-2">No Role Selected</h3>
                        <p className="text-sm text-slate-400 max-w-xs mx-auto">
                            Please select a role from the dropdown above to manage its access permissions and security settings.
                        </p>
                    </div>
                </div>
            ) : roleLoading ? (
                <div className="p-20 text-center bg-white rounded-[3rem] border border-slate-100 shadow-sm">
                    <div className="animate-pulse flex flex-col items-center">
                        <div className="h-4 w-48 bg-slate-100 rounded-full mb-4"></div>
                        <div className="h-8 w-64 bg-slate-50 rounded-full"></div>
                    </div>
                </div>
            ) : (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <RoleForm
                        formData={formData}
                        handleNameChange={handleNameChange}
                        handleDescriptionChange={handleDescriptionChange}
                        togglePermission={togglePermission}
                        handleQuickAction={handleQuickAction}
                        isSaving={isSaving}
                        onSave={handleSave}
                        showSaveButton={false}
                    />
                </div>
            )}
        </div>
    );
};

export default PermissionManagement;
